import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  notificationChannelSchema,
  notificationDeliverySchema,
  notificationRuleSchema,
  testChannelResponseSchema,
  type CreateNotificationChannel,
  type CreateNotificationRule,
  type NotificationChannel,
  type NotificationDelivery,
  type NotificationRule,
  type TestChannelResponse,
  type UpdateNotificationChannel,
  type UpdateNotificationRule,
} from '@netdeck/contracts';
import { z } from 'zod';
import { apiRequest } from '../../shared/api/client';

export const channelsKey = ['notifications', 'channels'] as const;
export const rulesKey = ['notifications', 'rules'] as const;
export const deliveriesKey = ['notifications', 'deliveries'] as const;

export function useChannels(): UseQueryResult<NotificationChannel[], Error> {
  return useQuery({
    queryKey: channelsKey,
    queryFn: () => apiRequest('/notifications/channels', z.array(notificationChannelSchema)),
  });
}

export function useRules(): UseQueryResult<NotificationRule[], Error> {
  return useQuery({
    queryKey: rulesKey,
    queryFn: () => apiRequest('/notifications/rules', z.array(notificationRuleSchema)),
  });
}

/**
 * Журнал доставок обновляется сам раз в полминуты.
 *
 * Настраивают уведомления так: правишь канал, ждёшь аварии или жмёшь
 * «проверить», смотришь, дошло ли. Без автообновления пришлось бы жать F5,
 * и это ровно то место, где человек решает, что «ничего не работает».
 */
export function useDeliveries(): UseQueryResult<NotificationDelivery[], Error> {
  return useQuery({
    queryKey: deliveriesKey,
    queryFn: () => apiRequest('/notifications/deliveries', z.array(notificationDeliverySchema)),
    refetchInterval: 30_000,
  });
}

export function useCreateChannel(): UseMutationResult<
  NotificationChannel,
  Error,
  CreateNotificationChannel
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      apiRequest('/notifications/channels', notificationChannelSchema, {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: channelsKey }),
  });
}

export function useUpdateChannel(): UseMutationResult<
  NotificationChannel,
  Error,
  { id: string; input: UpdateNotificationChannel }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) =>
      apiRequest(`/notifications/channels/${id}`, notificationChannelSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: channelsKey });
      // Правила показывают имя и вид канала: переименованный канал обязан
      // смениться и в списке правил, иначе они начнут спорить.
      await queryClient.invalidateQueries({ queryKey: rulesKey });
    },
  });
}

// Ответ на удаление — 204 без тела; схема `unknown`, как и у остальных
// удалений в платформе: `void` не разбирает пустой ответ.
export function useDeleteChannel(): UseMutationResult<unknown, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      apiRequest(`/notifications/channels/${id}`, z.unknown(), { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: channelsKey });
      // Правила канала уходят вместе с ним каскадом.
      await queryClient.invalidateQueries({ queryKey: rulesKey });
    },
  });
}

export function useTestChannel(): UseMutationResult<TestChannelResponse, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      apiRequest(`/notifications/channels/${id}/test`, testChannelResponseSchema, {
        method: 'POST',
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: deliveriesKey }),
  });
}

export function useCreateRule(): UseMutationResult<
  NotificationRule,
  Error,
  CreateNotificationRule
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input) =>
      apiRequest('/notifications/rules', notificationRuleSchema, { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rulesKey }),
  });
}

export function useUpdateRule(): UseMutationResult<
  NotificationRule,
  Error,
  { id: string; input: UpdateNotificationRule }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }) =>
      apiRequest(`/notifications/rules/${id}`, notificationRuleSchema, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rulesKey }),
  });
}

export function useDeleteRule(): UseMutationResult<unknown, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) => apiRequest(`/notifications/rules/${id}`, z.unknown(), { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: rulesKey }),
  });
}
